import React, { useState, useEffect, useRef } from 'react';
import { QuestionService, QuestionVersion } from '../../services/QuestionService';
import { QuestionStatus, DataType, Category } from '../../types/enums';

interface QuestionEditionProps {
  question: { id: number; question: string; level: number; status: QuestionStatus; category: Category; vector_db_data: string; data_type: DataType; versions: QuestionVersion[]; additional_info?: string; };
  onClose: () => void;
  onUpdate: () => void;
}

const selectClass = "w-full px-3 py-2 border border-[#ccc] rounded text-sm focus:outline-none focus:border-[#4a90e2] transition-colors bg-white";
const textareaClass = "w-full px-3 py-2 border border-[#ccc] rounded text-sm focus:outline-none focus:border-[#4a90e2] transition-colors resize-y";
const labelClass = "font-semibold text-[#4a5568] text-sm mb-1 block";
const glowBtn: React.CSSProperties = { background: 'linear-gradient(120deg, #0b3d91, #1e68ff)', color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 20px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 10px 26px rgba(14,52,161,0.35)', transition: 'transform 0.15s ease' };
const cancelBtn: React.CSSProperties = { background: 'linear-gradient(120deg, #f2f6ff, #e4ebff)', color: '#0c1a36', border: '1px solid #dfe7ff', borderRadius: '10px', padding: '10px 20px', fontWeight: 700, cursor: 'pointer' };
const deleteBtn: React.CSSProperties = { background: 'linear-gradient(120deg, #b3182a, #e7384f)', color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 20px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 20px rgba(194,43,60,0.3)' };

const QuestionEdition: React.FC<QuestionEditionProps> = ({ question, onClose, onUpdate }) => {
  const [editing, setEditing] = useState({ id: question.id, content: question.question, level: question.level, status: question.status, category: question.category, vector_db_data: question.vector_db_data, data_type: question.data_type, additional_info: question.additional_info || '' });
  const [newVersion, setNewVersion] = useState<string>('');
  const [versions, setVersions] = useState<QuestionVersion[]>(question.versions);
  const modalRef = useRef<HTMLDivElement>(null);
  const [deletingVersionId, setDeletingVersionId] = useState<number | null>(null);
  const [isAddingVersion, setIsAddingVersion] = useState(false);
  const [isDeletingQuestion, setIsDeletingQuestion] = useState(false);
  const [isUpdatingQuestion, setIsUpdatingQuestion] = useState(false);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => { if (modalRef.current && !modalRef.current.contains(e.target as Node)) onClose(); };
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
  }, [onClose]);

  const handleCreateVersion = async () => {
    if (!newVersion.trim() || isAddingVersion) return;
    try {
      setIsAddingVersion(true);
      const response = await QuestionService.createQuestionVersion(question.id, { version_question: newVersion.trim() });
      setVersions([...versions, response]); setNewVersion(''); onUpdate();
    } catch (err) { console.error('Error creating version:', err); } finally { setIsAddingVersion(false); }
  };

  const handleDeleteVersion = async (versionId: number) => {
    if (deletingVersionId !== null) return;
    try {
      setDeletingVersionId(versionId);
      await QuestionService.deleteQuestionVersion(versionId);
      setVersions(prev => prev.filter(v => v.id !== versionId)); onUpdate();
    } catch (e) { console.error('Error deleting version:', e); } finally { setDeletingVersionId(null); }
  };

  const handleDeleteQuestion = async () => {
    if (isDeletingQuestion) return;
    try { setIsDeletingQuestion(true); await QuestionService.deleteQuestion(editing.id); onUpdate(); onClose(); }
    catch (e) { console.error('Error deleting question:', e); setIsDeletingQuestion(false); }
  };

  const handleUpdateQuestion = async () => {
    if (isUpdatingQuestion) return;
    try {
      setIsUpdatingQuestion(true);
      await QuestionService.updateQuestion(editing.id, { question: editing.content, level: editing.level, status: editing.status, category: editing.category, vector_db_data: editing.vector_db_data, data_type: editing.data_type, versions: [], additional_info: editing.additional_info || '' });
      onClose(); onUpdate();
    } catch (e) { console.error('Error updating question:', e); setIsUpdatingQuestion(false); }
  };

  const set = (key: string, value: unknown) => setEditing(prev => ({ ...prev, [key]: value }));

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div ref={modalRef} className="bg-white rounded-xl w-[90%] max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <h3 className="m-0 text-xl font-bold text-[#2d3748]">Editar Questão #{question.id}</h3>
          <button className="bg-transparent border-none text-2xl cursor-pointer text-[#718096] hover:text-[#2d3748] transition-colors" onClick={onClose}>×</button>
        </div>

        <div className="p-6 flex flex-col gap-4 overflow-y-auto">
          {/* Question fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><label className={labelClass} htmlFor="qe-question">Pergunta:</label>
              <textarea id="qe-question" className={textareaClass} value={editing.content} onChange={e => set('content', e.target.value)} rows={2} />
            </div>
            <div><label className={labelClass} htmlFor="qe-level">Nível:</label>
              <select id="qe-level" className={selectClass} value={editing.level} onChange={e => set('level', parseInt(e.target.value))}>
                <option value="1">1</option><option value="2">2</option><option value="3">3</option>
              </select>
            </div>
            <div><label className={labelClass} htmlFor="qe-status">Status:</label>
              <select id="qe-status" className={selectClass} value={editing.status} onChange={e => set('status', e.target.value)}>
                {Object.values(QuestionStatus).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className={labelClass} htmlFor="qe-category">Categoria:</label>
              <select id="qe-category" className={selectClass} value={editing.category} onChange={e => set('category', e.target.value)}>
                {Object.values(Category).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label className={labelClass} htmlFor="qe-data-type">Tipo de Dado:</label>
              <select id="qe-data-type" className={selectClass} value={editing.data_type} onChange={e => set('data_type', e.target.value)}>
                {Object.values(DataType).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="md:col-span-2"><label className={labelClass} htmlFor="qe-vector">Dados do Banco de Vetores:</label>
              <textarea id="qe-vector" className={textareaClass} value={editing.vector_db_data || ''} onChange={e => set('vector_db_data', e.target.value)} rows={2} placeholder="Opcional..." />
            </div>
            <div className="md:col-span-2"><label className={labelClass} htmlFor="qe-info">Informações Adicionais:</label>
              <textarea id="qe-info" className={textareaClass} value={editing.additional_info || ''} onChange={e => set('additional_info', e.target.value)} placeholder="Opcional..." rows={2} />
            </div>
          </div>

          {/* Versions */}
          <div className="border-t border-[#e2e8f0] pt-4">
            <h4 className="m-0 mb-3 text-base font-bold text-[#2d3748]">Versões de Questão</h4>
            {versions?.map(v => (
              <div key={v.id} className="flex items-start justify-between gap-2 p-3 mb-2 bg-[#f8f9fa] border border-[#e2e8f0] rounded-lg">
                <div className="flex flex-col gap-1 flex-1">
                  <span className="text-xs font-bold text-[#6c757d]">ID: {v.id}</span>
                  <p className="m-0 text-sm text-[#333]">{v.version_question}</p>
                </div>
                <button className="bg-red-100 text-red-700 border border-red-200 rounded px-2 py-1 text-xs cursor-pointer hover:bg-red-200 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={e => { e.stopPropagation(); if (deletingVersionId === null) handleDeleteVersion(v.id ?? 0); }}
                  disabled={deletingVersionId !== null}>
                  {deletingVersionId === v.id ? 'Removendo...' : 'Remover Versão'}
                </button>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <textarea className={textareaClass + " flex-1"} value={newVersion} onChange={e => setNewVersion(e.target.value)} placeholder="Digite o conteúdo da nova versão..." rows={2} />
              <button className="bg-[#4a90e2] text-white border-none rounded px-4 py-2 font-medium cursor-pointer hover:bg-[#357abd] transition-colors whitespace-nowrap self-start mt-1 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={handleCreateVersion} disabled={!newVersion.trim() || isAddingVersion}>
                {isAddingVersion ? 'Adicionando...' : 'Adicionar Nova Versão'}
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 pb-5 border-t border-[#e2e8f0] pt-4">
          <button style={cancelBtn} onClick={onClose}>Cancelar</button>
          <button style={deleteBtn} onClick={handleDeleteQuestion} disabled={isDeletingQuestion} className="disabled:opacity-60 disabled:cursor-not-allowed">{isDeletingQuestion ? 'Deletando...' : 'Deletar Pergunta'}</button>
          <button style={glowBtn} onClick={handleUpdateQuestion} disabled={isUpdatingQuestion} className="disabled:opacity-60 disabled:cursor-not-allowed">{isUpdatingQuestion ? 'Salvando...' : 'Salvar Alterações'}</button>
        </div>
      </div>
    </div>
  );
};

export default QuestionEdition;